/**
 * @fileoverview The persistent "Share feedback" control (Phase One, step 5).
 *
 * Lives in the studio sidebar, on every screen, for founding members. The
 * cohort's whole purpose is to say what is wrong at the moment it goes wrong,
 * and feedback that has to be remembered until the end of the day is feedback
 * that never gets sent.
 *
 * Three deliberate choices:
 *
 * * **It captures the page automatically.** Which screen someone was on is the
 *   single most useful line in a bug report and the one nobody thinks to type.
 * * **It never navigates.** The existing public `/feedback` page opens a
 *   `mailto:` and leaves the app; doing that mid-lesson would be an unusually
 *   cruel place to lose a recording. This is a dialog over whatever they were
 *   doing.
 * * **A failed send keeps the text on screen.** The message is something the
 *   teacher wrote; closing the dialog and toasting an error would throw it away
 *   and teach them not to bother a second time.
 */

import { useContext, useState } from "react";
import { useLocation } from "react-router-dom";
import { Loader2, MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";

import { AuthContext } from "@/App";
import api, { getErrorMessage } from "@/lib/api";
import { FEEDBACK_CATEGORIES, isFoundingMember } from "@/lib/founding";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function FeedbackButton({ onDone }) {
  const { user } = useContext(AuthContext) || {};
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [category, setCategory] = useState("idea");
  const [message, setMessage] = useState("");

  // Surfaced to the cohort only. The ENDPOINT is open to every instructor —
  // refusing feedback from a paying customer because they are not in a
  // marketing programme would be indefensible — so widening this later is a
  // one-line change here and nothing on the server.
  if (!isFoundingMember(user)) return null;

  const send = async (e) => {
    e.preventDefault();
    const body = message.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      await api.post("/feedback", {
        message: body,
        category,
        page: location.pathname,
        client: "web",
      });
      setMessage("");
      setCategory("idea");
      setOpen(false);
      onDone?.();
      toast.success("Thank you — we read every one of these.");
    } catch (err) {
      // Deliberately leaves the dialog open with the text intact.
      toast.error(getErrorMessage(err, "We couldn't send that. Your note is still here — try again?"));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="feedback-button"
        className="flex items-center gap-[14px] rounded-[12px] border border-transparent px-[14px] py-[11px] text-left transition-colors duration-150 hover:bg-[var(--studio-raised)]"
      >
        <MessageSquarePlus size={20} strokeWidth={1.7} className="text-[var(--accent-yellow-text)]" />
        <span className="flex-1 font-sans text-base font-medium text-[var(--text-secondary)]">Share feedback</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl font-light">Share feedback</DialogTitle>
          </DialogHeader>

          <form onSubmit={send} className="space-y-4">
            <p className="font-sans text-sm text-[var(--text-tertiary)]">
              Half-formed is fine. We'd rather hear it now than after you've talked yourself out of it.
            </p>

            <div className="grid grid-cols-2 gap-2">
              {FEEDBACK_CATEGORIES.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setCategory(c.value)}
                  aria-pressed={category === c.value}
                  className={[
                    "rounded-[10px] border px-3 py-2 font-sans text-sm transition-colors",
                    category === c.value
                      ? "border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)] text-[var(--accent-yellow-text)]"
                      : "border-[var(--studio-divider)] text-[var(--text-secondary)] hover:bg-[var(--studio-raised)]",
                  ].join(" ")}
                >
                  {c.label}
                </button>
              ))}
            </div>

            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              maxLength={5000}
              autoFocus
              placeholder="What happened, or what would you change?"
              data-testid="feedback-message"
              className="w-full rounded-[10px] border border-[var(--studio-divider)] bg-[var(--studio-base)] px-3.5 py-2.5 font-sans text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent-yellow-border)]"
            />

            <div className="flex items-center justify-between gap-3">
              <span className="font-mono text-[11px] text-[var(--text-tertiary)]">{location.pathname}</span>
              <Button
                type="submit"
                disabled={!message.trim() || sending}
                data-testid="feedback-send"
                className="rounded-full bg-[var(--accent-yellow)] px-6 font-sans text-sm font-semibold text-[var(--text-inverse)] disabled:opacity-40"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
